<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  disablePushNotifications,
  getNotificationPermission,
  isIOSBrowserTab,
  requestPushPermissions,
} from 'dashboard/helper/pushHelper.js';
import { onPageVisible } from 'dashboard/helper/pageVisibility';
import {
  devicePushState,
  refreshDevicePushState,
} from 'dashboard/helper/pushDeviceState';
import {
  isReconnecting,
  syncAfterPushChange,
} from 'dashboard/helper/pushRecovery';
import { detectOS, OS } from 'shared/helpers/platform';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { NOTIFICATION_GROUPS } from './constants';

export default {
  components: {
    TableHeaderCell,
    ToggleSwitch,
    CheckBox,
    Spinner,
  },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      notificationGroups: NOTIFICATION_GROUPS,
      // BonBoZ: quyền thông báo của trình duyệt trên MÁY NÀY.
      // 'default' chưa hỏi · 'granted' đã cấp · 'denied' đã từ chối · 'unsupported'
      notificationPermission: getNotificationPermission(),
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    // BonBoZ: trạng thái của dòng "Thông báo trên máy này".
    // Có đăng ký push mới là thật sự nhận được tin; quyền `granted` mà chưa
    // đăng ký (tự tắt, hoặc đăng ký hỏng) thì vẫn là đang tắt — trừ khi app
    // đang tự đăng ký lại cho máy này.
    notificationStatus() {
      if (this.notificationPermission === 'unsupported') {
        // iPhone trong tab Safari không có `Notification`, nhưng cài app ra
        // màn hình chính là dùng được — đừng báo "không hỗ trợ".
        return isIOSBrowserTab() ? 'CAN_CAI_APP_IOS' : 'KHONG_HO_TRO';
      }
      // Android còn phải vuốt tắt hẳn app rồi mở lại thì Chrome mới đọc quyền mới.
      if (this.notificationPermission === 'denied') {
        return detectOS() === OS.ANDROID ? 'BLOCKED_ANDROID' : 'BI_CHAN';
      }
      if (this.hasEnabledPushPermissions) return 'DA_BAT';
      if (isReconnecting.value) return 'DANG_KET_NOI_LAI';
      if (this.notificationPermission === 'granted') return 'DANG_TAT';
      return 'CHUA_HOI';
    },
    // Không hỗ trợ, hoặc đã bị chặn (trình duyệt không hỏi lại) thì bật cũng
    // vô ích — chỉ để lại hướng dẫn.
    canTogglePush() {
      return ![
        'CAN_CAI_APP_IOS',
        'KHONG_HO_TRO',
        'BI_CHAN',
        'BLOCKED_ANDROID',
      ].includes(this.notificationStatus);
    },
    // Đang tự kết nối lại: khoá công tắc, kẻo bấm chen vào lượt đang chạy.
    isReconnectingHere() {
      return this.notificationStatus === 'DANG_KET_NOI_LAI';
    },
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    // Nhóm nào đòi SLA thì ẩn cả nhóm khi tài khoản chưa bật.
    visibleGroups() {
      return this.notificationGroups.filter(
        group => this.isSLAEnabled || !group.requiresSla
      );
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedEmailFlags = value;
    },
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
  },
  mounted() {
    // BonBoZ: công tắc đi theo trạng thái dùng chung của máy này, nên app tự
    // đăng ký lại được hay banner vừa bật thì công tắc cũng đổi ngay. Chặn/bỏ
    // chặn trong Cài đặt máy thì trang không nhận sự kiện nào, nên đọc lại mỗi
    // lần quay lại màn hình. Trạng thái đã đọc từ trước thì áp dụng ngay.
    this.$watch(() => devicePushState.value, this.applyPushState, {
      immediate: true,
    });
    refreshDevicePushState();
    this.stopVisibilityListener = onPageVisible(refreshDevicePushState);
    this.$store.dispatch('userNotificationSettings/get');
  },
  unmounted() {
    this.stopVisibilityListener?.();
  },
  methods: {
    checkFlagStatus(type, flagType) {
      const selectedFlags =
        type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;
    },
    // `v-model` đã bật công tắc trước khi biết kết quả; không thành thì trả về
    // tắt, kẻo công tắc sáng mà không có đăng ký nào phía sau.
    onRegistrationError() {
      this.hasEnabledPushPermissions = false;
    },
    async onRequestPermissions(value) {
      if (value) {
        // Xin quyền (nếu chưa) VÀ đăng ký push trong cùng một cú bấm: trình
        // duyệt chỉ cho `requestPermission()` chạy từ thao tác thật của người
        // dùng, và chỉ có quyền mà chưa đăng ký thì server không gửi tới được.
        await requestPushPermissions({
          onSuccess: () => {
            this.onRegistrationSuccess();
            useAlert(this.$t('BONBOZ.QUYEN_THONG_BAO.DA_CAP'));
          },
          onError: this.onRegistrationError,
        });
      } else {
        await this.disablePush();
      }
      // Công tắc, banner và server phản ánh ngay thao tác vừa làm.
      await syncAfterPushChange();
    },
    // Server phải biết trước khi huỷ đăng ký trên máy; không báo được (mất
    // mạng) thì giữ công tắc bật để người dùng thử lại.
    async disablePush() {
      const isDisabled = await disablePushNotifications();
      this.hasEnabledPushPermissions = !isDisabled;
      if (!isDisabled) {
        useAlert(this.$t('BONBOZ.QUYEN_THONG_BAO.DISABLE_FAILED'));
      }
    },
    // Chỉ coi là đang bật khi quyền còn `granted` VÀ còn đăng ký push.
    applyPushState({ permission, hasSubscription }) {
      this.notificationPermission = permission;
      if (hasSubscription === null) return;
      this.hasEnabledPushPermissions =
        permission === 'granted' && hasSubscription;
    },
    async updateNotificationSettings() {
      try {
        this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handleInput(type, id) {
      if (type === 'email') {
        this.handleEmailInput(id);
      } else {
        this.handlePushInput(id);
      }
    },
    handleEmailInput(id) {
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      this.updateNotificationSettings();
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <!-- Layout for desktop devices -->
    <div class="hidden sm:block">
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="2"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-heading-3 normal-case text-n-slate-12 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
        </TableHeaderCell>
      </div>
      <div v-for="group in visibleGroups" :key="group.title">
        <div class="pt-4 pb-1">
          <span class="text-heading-3 uppercase text-n-slate-11">
            {{ $t(group.title) }}
          </span>
        </div>
        <div
          v-for="notification in group.types"
          :key="notification.value"
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 col-span-7 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
          >
            <span class="text-body-main text-n-slate-12">
              {{ $t(notification.label) }}
            </span>
          </div>
          <div
            v-for="(type, typeIndex) in ['email', 'push']"
            :key="typeIndex"
            class="flex items-start gap-2 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
            :class="`col-span-${type === 'push' ? 3 : 2}`"
          >
            <CheckBox
              v-if="type === 'push' || notification.email !== false"
              :value="`${type}_${notification.value}`"
              :is-checked="
                checkFlagStatus(type, notification.value, selectedPushFlags)
              "
              @update="id => handleInput(type, id)"
            />
            <span
              v-else
              class="text-n-slate-10"
              :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.NO_EMAIL')"
            >
              —
            </span>
          </div>
        </div>
      </div>
    </div>
    <!--  Layout for mobile devices -->
    <div class="flex flex-col gap-6 sm:hidden">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div class="flex flex-col gap-4">
        <div v-for="group in visibleGroups" :key="group.title">
          <span class="text-heading-3 uppercase text-n-slate-11">
            {{ $t(group.title) }}
          </span>
          <div class="flex flex-col gap-4 pt-3">
            <div
              v-for="notification in group.types.filter(
                item => item.email !== false
              )"
              :key="notification.value"
              class="flex flex-row items-start gap-2"
            >
              <CheckBox
                :id="`email_${notification.value}`"
                :value="`email_${notification.value}`"
                :is-checked="checkFlagStatus('email', notification.value)"
                @update="handleEmailInput"
              />
              <span class="text-body-main text-n-slate-12">{{
                $t(notification.label)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-start gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>

      <div class="flex flex-col gap-4">
        <div v-for="group in visibleGroups" :key="group.title">
          <span class="text-heading-3 uppercase text-n-slate-11">
            {{ $t(group.title) }}
          </span>
          <div class="flex flex-col gap-4 pt-3">
            <div
              v-for="notification in group.types"
              :key="notification.value"
              class="flex flex-row items-start gap-2"
            >
              <CheckBox
                :id="`push_${notification.value}`"
                :value="`push_${notification.value}`"
                :is-checked="checkFlagStatus('push', notification.value)"
                @update="handlePushInput"
              />
              <span class="text-body-main text-n-slate-12">{{
                $t(notification.label)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BonBoZ: chỉ một dòng cho thông báo trên máy này. Thông báo hệ thống
         giờ chỉ đến từ Web Push của server (trang web không tự tạo thông báo
         nữa), nên "quyền trình duyệt" và "đăng ký push" là một việc: công tắc
         bật = xin quyền + đăng ký, tắt = huỷ đăng ký. -->
    <div
      class="flex items-center justify-between w-full gap-4 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-col gap-1">
        <span class="text-body-main text-n-slate-12">
          {{ $t('BONBOZ.QUYEN_THONG_BAO.NHAN') }}
        </span>
        <span class="text-sm text-n-slate-11" data-testid="device-push-status">
          {{ $t(`BONBOZ.QUYEN_THONG_BAO.${notificationStatus}`) }}
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Spinner
          v-if="isReconnectingHere"
          :size="16"
          class="text-n-slate-11"
          data-testid="device-push-reconnecting"
        />
        <ToggleSwitch
          v-if="canTogglePush"
          v-model="hasEnabledPushPermissions"
          :disabled="isReconnectingHere"
          class="disabled:opacity-60 disabled:cursor-not-allowed"
          @change="onRequestPermissions"
        />
      </div>
    </div>
  </div>
</template>
